import { extendTheme } from "@chakra-ui/react";
import { mode, type StyleFunctionProps } from "@chakra-ui/theme-tools";

const config = {
  initialColorMode: "light",
  useSystemColorMode: false,
};

const ggTheme = extendTheme({
  config,
  colors: {
    // Onfly Design System — brand colors (color.brand.*)
    brand: {
      50: "#eff8ff",
      100: "#def0ff",
      200: "#b6e3ff",
      300: "#75ceff",
      400: "#2cb5ff",
      500: "#009EFB",
      600: "#007bd4",
      700: "#0062ab",
      800: "#00538d",
      900: "#064574",
      950: "#042c4d",
    },
    // Onfly Design System — gray scale (color.gray.*)
    gray: {
      50: "#f5f5f6",
      100: "#e6e6e7",
      200: "#cfd0d2",
      300: "#adadb3",
      400: "#84858c",
      500: "#696a71",
      600: "#58585f",
      700: "#4c4c52",
      800: "#434347",
      900: "#3b3b3e",
      950: "#252527",
    },
    // Legacy aliases (para compatibilidade durante a migração)
    ggBlue: {
      50: "#eff8ff", 100: "#def0ff", 200: "#b6e3ff", 300: "#75ceff",
      400: "#2cb5ff", 500: "#009EFB", 600: "#007bd4", 700: "#0062ab",
      800: "#00538d", 900: "#064574",
    },
    ggGray: {
      50: "#f5f5f6", 100: "#e6e6e7", 200: "#cfd0d2", 300: "#adadb3",
      400: "#84858c", 500: "#696a71", 600: "#58585f", 700: "#4c4c52",
    },
    ggSurface: { light: "#f5f5f6", dark: "#252527" },
    ggCard: { light: "#ffffff", dark: "#3b3b3e" },
  },
  semanticTokens: {
    colors: {
      "chakra-body-text": {
        _light: "#252527",
        _dark: "#e6e6e7",
      },
      "chakra-body-bg": {
        _light: "#f5f5f6",
        _dark: "#252527",
      },
    },
  },
  fonts: {
    body: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
    heading:
      'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
    mono: "Menlo, monospace",
  },
  radii: {
    xs: "4px",
    sm: "8px",
    md: "12px",
    lg: "16px",
    xl: "24px",
  },
  shadows: {
    xs: "0px 1px 2px 0px rgba(9,11,14,0.02)",
    sm: "0px 1px 2px -1px rgba(9,11,14,0.02), 0px 1px 2px 0px rgba(9,11,14,0.04)",
    md: "0px 2px 4px -2px rgba(9,11,14,0.02), 0px 4px 6px -1px rgba(9,11,14,0.04)",
    lg: "0px 4px 6px -4px rgba(9,11,14,0.02), 0px 12px 16px -4px rgba(9,11,14,0.08)",
    xl: "0px 8px 10px -6px rgba(9,11,14,0.02), 0px 20px 25px -5px rgba(9,11,14,0.1)",
    "2xl": "0px 24px 48px -12px rgba(9,11,14,0.1)",
    popup: "0px 2px 8px 4px rgba(9,11,14,0.03)",
    dropdown: "2px 4px 4px 0px rgba(9,11,14,0.03)",
    button: "0px 2px 4px 0px rgba(9,11,14,0.06)",
    input: "0px 1px 4px 0px rgba(9,11,14,0.02)",
  },
  styles: {
    global: (props: StyleFunctionProps) => ({
      "html, body": {
        backgroundColor: mode("#f5f5f6", "#252527")(props),
        color: mode("#252527", "#e6e6e7")(props),
        overflowX: "hidden",
        fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
        letterSpacing: "-0.01em",
      },
      // Focus ring: preto no claro, azul da marca no escuro.
      "button:focus-visible, input:focus-visible, select:focus-visible, textarea:focus-visible, a:focus-visible, [role='button']:focus-visible": {
        outline: `2px solid ${mode("#000", "#2cb5ff")(props)}`,
        outlineOffset: "2px",
      },
      "::-webkit-scrollbar": {
        width: "6px",
        height: "6px",
      },
      "::-webkit-scrollbar-track": {
        background: "transparent",
      },
      "::-webkit-scrollbar-thumb": {
        background: "#2cb5ff",
        borderRadius: "full",
      },
      "::-webkit-scrollbar-thumb:hover": {
        background: "#009EFB",
      },
    }),
  },
  components: {
    Button: {
      baseStyle: {
        fontWeight: 500,
        borderRadius: "md",
        letterSpacing: "-0.01em",
      },
      defaultProps: {
        colorScheme: "brand",
      },
    },
    Input: {
      defaultProps: {
        focusBorderColor: "#009EFB",
      },
    },
    Select: {
      defaultProps: {
        focusBorderColor: "#009EFB",
      },
    },
    Textarea: {
      defaultProps: {
        focusBorderColor: "#009EFB",
      },
    },
    Link: {
      baseStyle: {
        color: "#009EFB",
      _hover: { color: "#007bd4" },
      },
    },
    Modal: {
      baseStyle: {
        dialog: { _dark: { bg: "#0f1f3a" } },
      },
    },
    Drawer: {
      baseStyle: {
        dialog: { _dark: { bg: "#0f1f3a" } },
      },
    },
    Popover: {
      baseStyle: {
        content: { _dark: { bg: "#0f1f3a" } },
      },
    },
    Tooltip: {
      baseStyle: {
        "--tooltip-bg": "#4c4c52",
        _dark: { "--tooltip-bg": "#0f1f3a" },
      },
    },
  },
});

export default ggTheme;